import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, Input, Select, Button, Space } from 'antd';
import {
  timeTrackerProjectCreateService,
  timeTrackerProjectUpdateService,
  timeTrackerProjectGetService,
  timeTrackerUserGroupsListService,
  timeTrackerAssignableUsersListService,
} from 'services/time_tracker';
import { companiesListService } from 'services/company';
import { branchesListService } from 'services/branch';
import { handleApiSuccess, handleApiError, accessControls } from 'utils';
import { getProjectColor } from 'utils/time_tracker_project_color';
import { timeTrackerAssignableUserLabel } from 'utils/time_tracker_assignable_user_label';

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};
const tailLayout = {
  wrapperCol: { offset: 6, span: 14 },
};

const { Option } = Select;
const { TextArea } = Input;

function branchCompanyId(branch) {
  if (!branch || !branch.company) return null;
  const c = Array.isArray(branch.company) ? branch.company[0] : branch.company;
  return c && (c._id || c);
}

export default function TimeTrackerProjectForm(props) {
  const { id } = props;
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const [form] = Form.useForm();
  const [companies, setCompanies] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [perms, setPerms] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const branchesForCompany = useMemo(() => {
    if (!selectedCompany) return [];
    return (allBranches || [])
      .filter((b) => {
        const cid = branchCompanyId(b);
        return cid && String(cid) === String(selectedCompany);
      })
      .sort((a, b) =>
        (a.branch_name || '').localeCompare(b.branch_name || '', undefined, {
          sensitivity: 'base',
        }),
      );
  }, [allBranches, selectedCompany]);

  useEffect(() => {
    companiesListService()
      .then((res) => {
        if (res.data.items && res.data.items.length > 0) {
          setCompanies(res.data.items);
        }
      })
      .catch(handleApiError);

    branchesListService()
      .then((res) => {
        if (res.data.items && res.data.items.length > 0) {
          setAllBranches(res.data.items);
        }
      })
      .catch(handleApiError);

    timeTrackerAssignableUsersListService()
      .then((res) => {
        if (res.data.items && res.data.items.length > 0) {
          setUsers(res.data.items);
        }
      })
      .catch(handleApiError);

    accessControls
      .fetch()
      .then((p) => {
        setPerms(p || {});
        if (accessControls.can(p || {}, 'TimeTrackerUserGroup.read:any')) {
          timeTrackerUserGroupsListService()
            .then((res) => setUserGroups(res.data?.items || []))
            .catch(() => setUserGroups([]));
        }
      })
      .catch(() => setPerms({}));

    if (id) {
      timeTrackerProjectGetService(id)
        .then((res) => {
          if (res.data.items && res.data.items.length > 0) {
            const record = res.data.items[0];
            const companyId =
              record.company?._id || record.company;
            const branchId = record.branch?._id || record.branch;
            form.setFieldsValue({
              company: companyId,
              branch: branchId,
              project_name: record.project_name,
              project_description: record.project_description || '',
              color: getProjectColor(record),
              team: (record.team || []).map((u) => (u._id ? u._id : u)),
              assigned_groups: (record.assigned_groups || []).map((g) =>
                g._id ? g._id : g,
              ),
              status: record.status || 'active',
            });
            if (companyId) setSelectedCompany(companyId);
          }
        })
        .catch(handleApiError);
    }
  }, [id, form]);

  const onFinish = (values) => {
    setIsLoading(true);
    const payload = {
      company: values.company,
      branch: values.branch,
      project_name: values.project_name,
      project_description: values.project_description || '',
      color: values.color,
      team: values.team || [],
      assigned_groups: values.assigned_groups || [],
      status: values.status || 'active',
    };
    const promise = id
      ? timeTrackerProjectUpdateService(id, payload)
      : timeTrackerProjectCreateService(payload);
    promise
      .then((response) => {
        setIsLoading(false);
        handleApiSuccess(response);
        history.push('/time-tracker/projects');
      })
      .catch((err) => {
        setIsLoading(false);
        handleApiError(err);
      });
  };

  return (
    <Form
      {...layout}
      form={form}
      name="time-tracker-project"
      onFinish={onFinish}
      initialValues={{ status: 'active', color: '#1890ff' }}
    >
      <Form.Item
        name="company"
        label="Company"
        rules={[{ required: true, message: 'Select a company' }]}
      >
        <Select
          placeholder="Select company"
          showSearch
          optionFilterProp="children"
          onKeyDown={(e) => {
            const listId = e.target.getAttribute('aria-controls');
            const { key } = e;
            if (key === 'Tab') {
              const dropdown = document
                .getElementById(listId)
                ?.parentElement?.querySelector('.ant-select-item-option-active');
              if (dropdown) dropdown.click();
            }
          }}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={(val) => {
            setSelectedCompany(val);
            form.setFieldsValue({ branch: undefined });
          }}
          options={[...companies]
            .sort((a, b) =>
              (a.company_name || '').localeCompare(b.company_name || '', undefined, {
                sensitivity: 'base',
              }),
            )
            .map((c) => ({
              value: c._id,
              label: c.company_name || c._id,
            }))}
        />
      </Form.Item>
      <Form.Item
        name="branch"
        label="Branch"
        rules={[{ required: true, message: 'Select a branch' }]}
      >
        <Select
          placeholder="Select branch"
          showSearch
          optionFilterProp="children"
          onKeyDown={(e) => {
            const listId = e.target.getAttribute('aria-controls');
            const { key } = e;
            if (key === 'Tab') {
              const dropdown = document
                .getElementById(listId)
                ?.parentElement?.querySelector('.ant-select-item-option-active');
              if (dropdown) dropdown.click();
            }
          }}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          disabled={!selectedCompany}
          options={branchesForCompany.map((b) => ({
            value: b._id,
            label: b.branch_name || b._id,
          }))}
        />
      </Form.Item>
      <Form.Item
        name="project_name"
        label="Project Name"
        rules={[{ required: true, message: 'Enter project name' }]}
      >
        <Input placeholder="Project name" />
      </Form.Item>
      <Form.Item name="project_description" label="Description">
        <TextArea rows={3} placeholder="Project description" />
      </Form.Item>
      <Form.Item
        name="color"
        label="Project color"
        extra="This color appears next to the project everywhere in Time Tracker (timer, entries, reports)."
      >
        <input
          type="color"
          aria-label="Project color"
          style={{
            width: 56,
            height: 36,
            padding: 2,
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            cursor: 'pointer',
            verticalAlign: 'middle',
            background: '#fff',
          }}
        />
      </Form.Item>
      <Form.Item
        name="assigned_groups"
        label="Access groups"
        extra={
          perms &&
          accessControls.can(perms, 'TimeTrackerUserGroup.read:any')
            ? 'At least one group is required before normal users can see this project. After that, members of these groups—as well as users on Team below or the project creator—can access it. Super Admin and similar privileged roles can always manage projects and appear on any team.'
            : 'You do not have permission to list user groups. Ask an administrator to assign groups, or request Time Tracker user group access.'
        }
      >
        <Select
          mode="multiple"
          allowClear
          placeholder="Select user groups that may access this project"
          showSearch
          optionFilterProp="label"
          disabled={
            !perms ||
            !accessControls.can(perms, 'TimeTrackerUserGroup.read:any')
          }
          onKeyDown={(e) => {
            const listId = e.target.getAttribute('aria-controls');
            const { key } = e;
            if (key === 'Tab') {
              const dropdown = document
                .getElementById(listId)
                ?.parentElement?.querySelector('.ant-select-item-option-active');
              if (dropdown) dropdown.click();
            }
          }}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={[...userGroups]
            .sort((a, b) =>
              (a.group_name || '').localeCompare(b.group_name || '', undefined, {
                sensitivity: 'base',
              }),
            )
            .map((g) => ({
              value: g._id,
              label: g.group_name || g._id,
            }))}
        />
      </Form.Item>
      <Form.Item
        name="team"
        label="Team"
        extra="Includes everyone under this reseller’s users list plus Super Admin / MSP staff (roles 10–13) who may not share the same client. Once this project has at least one access group, listed users here can use Time Tracker on it without being in a group. Super Admin always has full project access."
      >
        <Select
          mode="multiple"
          placeholder="Optional — tag users on this project"
          showSearch
          optionFilterProp="label"
          onKeyDown={(e) => {
            const listId = e.target.getAttribute('aria-controls');
            const { key } = e;
            if (key === 'Tab') {
              const dropdown = document
                .getElementById(listId)
                ?.parentElement?.querySelector('.ant-select-item-option-active');
              if (dropdown) dropdown.click();
            }
          }}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          dropdownRender={(menu) => (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  borderBottom: '1px solid #f0f0f0',
                  gap: 12,
                }}
              >
                <span
                  style={{ cursor: 'pointer', color: '#1890ff' }}
                  onClick={() =>
                    form.setFieldsValue({ team: users.map((u) => u._id) })
                  }
                >
                  Select all users
                </span>
                <span
                  style={{ cursor: 'pointer', color: '#1890ff' }}
                  onClick={() => form.setFieldsValue({ team: [] })}
                >
                  Clear all
                </span>
              </div>
              {menu}
            </>
          )}
          options={[...users]
            .map((u) => ({
              value: u._id,
              label: timeTrackerAssignableUserLabel(u),
            }))
            .sort((a, b) =>
              (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' })
            )}
        />
      </Form.Item>
      <Form.Item name="status" label="Status">
        <Select
          onKeyDown={(e) => {
            const listId = e.target.getAttribute('aria-controls');
            const { key } = e;
            if (key === 'Tab') {
              const dropdown = document
                .getElementById(listId)
                ?.parentElement?.querySelector('.ant-select-item-option-active');
              if (dropdown) dropdown.click();
            }
          }}
        >
          <Option value="active">Active</Option>
          <Option value="inactive">Inactive</Option>
          <Option value="archived">Archived</Option>
        </Select>
      </Form.Item>
      <Form.Item {...tailLayout}>
        <Space>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {id ? 'Update' : 'Create'}
          </Button>
          <Button onClick={() => history.push('/time-tracker/projects')}>
            Cancel
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
